import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, LabelProps } from "recharts";
import React from "react";
import "../../../../styles/network-performance/network-performance-charts.scss";


type BarData = {
  labelBackgroundColor: string;
  labelBorderColor: string;
  name: string;
  seg1: number;
  seg2: number;
  seg3: number;
  seg4: number;
  seg5: number;
  seg6: number;
};

const data: BarData[] = [
  { labelBackgroundColor: "#EFE9FA", labelBorderColor: "#a189cc", name: "Your Network", seg1: 10, seg2: 15, seg3: 10, seg4: 25, seg5: 30, seg6: 10 },
  { labelBackgroundColor: "#D6FBEE", labelBorderColor: "#6dc9a9", name: "Benchmark", seg1: 10, seg2: 15, seg3: 15, seg4: 35, seg5: 20, seg6: 5 },
  { labelBackgroundColor: "#FFF8E3", labelBorderColor: "#ccb366", name: "Scenario", seg1: 17, seg2: 10, seg3: 20, seg4: 15, seg5: 30, seg6: 8 },
];

type SegmentKey = "seg1" | "seg2" | "seg3" | "seg4" | "seg5" | "seg6";

const SEGMENT_LABELS: Record<SegmentKey, string> = {
  seg1: "5 Star",
  seg2: "4 Star",
  seg3: "3 Star",
  seg4: "2 Star",
  seg5: "1 Star",
  seg6: "RX Only",
};

type PayloadItem = {
  name: keyof BarData;
  value: number;
  fill: string;
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: PayloadItem[];
  label?: string | number;
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="networkPerformanceDistribution__tooltipContainer">
      <div className="networkPerformanceDistribution__toolTipTitle">Provider Distribution</div>

      {payload.map((entry, i) => (
        <div key={i} className="networkPerformanceDistribution__flexCenterRow">
          <div className="networkPerformanceDistribution__iconBox" style={{ "--icon-fill": entry.fill } as React.CSSProperties} />
          <span className="networkPerformanceDistribution__segmentLabelValue">
            {`${SEGMENT_LABELS[entry.name as SegmentKey] || entry.name} Total: ${entry.value}`}
          </span>
        </div>
      ))}
    </div>
  );
};

const CustomLabel = ({
  x,
  y,
  value,
  payload,
}: LabelProps & { payload?: BarData }) => {
  if (typeof x !== "number" || typeof y !== "number" || !value) return null;

  const backgroundColor = payload?.labelBackgroundColor || "#D6FBEE";
  const borderColor = payload?.labelBorderColor || "#C5FCE9";

  return (
    <g transform={`translate(${x},${y - 10})`}>
      <rect x={0} y={-15} width={88} height={22} fill={backgroundColor} rx={3} stroke={borderColor} strokeWidth={1} />
      <text
        x={5}
        y={0}
        fill="#000"
        fontSize={12}
        fontWeight={500}
        textAnchor="start"
      >
        {value}
      </text>
    </g>
  );
};

export default function NetworkDistributionChart(): React.JSX.Element {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ bottom: 20, left: 20, right: 20, top: 20 }}
        barCategoryGap="20%"
        barSize={25}
      >
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" hide />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />

        <Bar dataKey="seg1" stackId="a" fill="#9ADAC8">
          <LabelList
            dataKey="name"
            content={(props: LabelProps & { index: number }) => {
              const payload = data[props.index];
              return <CustomLabel {...props} payload={payload} />;
            }}
          />
        </Bar>
        <Bar dataKey="seg2" stackId="a" fill="#DBECFE" />
        <Bar dataKey="seg3" stackId="a" fill="#B7D4ED" />
        <Bar dataKey="seg4" stackId="a" fill="#9094C6" />
        <Bar dataKey="seg5" stackId="a" fill="#4F8071" />
        <Bar dataKey="seg6" stackId="a" fill="#D0F0B0" />
      </BarChart>
    </ResponsiveContainer>
  );
}
